import React from "react";
import { Form, Input, Button, message } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { LogoType } from "../assets";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import apiService from '../services/apiService';

const Login = ({ setIsAuthenticated, setUser }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await axios.post(apiService.admin.login(), {
        email: values.username,
        password: values.password
      });
      
      if (response.data.user.role !== 'admin') {
        throw new Error('Not admin');
      }

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      setIsAuthenticated(true);
      message.success('Connexion réussie');
      navigate('/dashboard');
    } catch (error) {
      console.error('Erreur de connexion:', error);
      if (error.message === 'Not admin') {
        message.error('Accès réservé aux administrateurs');
      } else if (error.response?.status === 401) {
        message.error('Email ou mot de passe incorrect');
      } else {
        message.error('Erreur lors de la connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-100 via-white to-slate-50">
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
      
      <div className="relative w-full max-w-md mx-auto">
        {/* Logo et titre */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <LogoType className="h-14 w-auto filter drop-shadow-xl transform hover:scale-105 transition-all duration-300" />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            Espace Administrateur
          </h2>
          <div className="mt-3 text-sm font-medium text-slate-600/80">
            Connectez-vous pour accéder à votre tableau de bord
          </div>
        </div>

        {/* Formulaire */}
        <div className="backdrop-blur-xl bg-white/60 p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100">
          <Form
            name="login"
            className="space-y-5"
            onFinish={onFinish}
            form={form}
          >
            <Form.Item
              name="username"
              rules={[
                { required: true, message: "Veuillez saisir votre email" },
                { type: "email", message: "Email invalide" }
              ]}
            >
              <Input
                prefix={<UserOutlined className="text-slate-400" />}
                placeholder="Email"
                size="large"
                className="h-12 rounded-xl bg-slate-50/50 border-slate-200 hover:border-slate-300 focus:border-primary-500 transition-all duration-300"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: "Veuillez saisir votre mot de passe" }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="text-slate-400" />}
                placeholder="Mot de passe"
                size="large"
                className="h-12 rounded-xl bg-slate-50/50 border-slate-200 hover:border-slate-300 focus:border-primary-500 transition-all duration-300"
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <Button
                type="primary"
                htmlType="submit"
                className="h-12 w-full bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 rounded-xl border-0 shadow-lg hover:shadow-primary-500/25 transition-all duration-300"
                loading={loading}
              >
                <span className="text-base font-medium">Se connecter</span>
              </Button>
            </Form.Item>
          </Form>
        </div>

        {/* Pattern décoratif */}
        <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-primary-500/5 to-purple-500/5 blur-3xl" />
      </div>
    </div>
  );
};

export default Login;
